import { useState, useEffect, useMemo, useRef } from "react";
import { flushSync } from "react-dom";
import { Popover, Input, Spin, Empty } from "antd";
import { createPortal } from "react-dom";
import { SearchOutlined } from "@ant-design/icons";
import classNames from "classnames";
import {
  getMessageTemplates,
  sendMessageTemplate,
  type MessageTemplate,
} from "@api/services/facebook";
import { MediaIcon } from "@components/icons/MediaIcon";
import { ButtonIcon } from "@components/icons/ButtonIcon";
import { GenericIcon } from "@components/icons/GenericIcon";
import { TemplatePreview } from "./TemplatePreview";
import type { Conversation, Message } from "@interfaces/chat.interface";
import { useAppSelector, useAppDispatch } from "@stores/redux/store";
import { addMessage } from "@features/messages/messagesSlice";
import { useChatStore } from "@stores/chatStore";
import { useNotificationContext } from "@hooks/useNotificationContext";
import { getDisplayNameFromToken } from "@utils/tokenUtils";
import { STORAGE_KEYS } from "@config/app.config";
import { getLocalStorage } from "@common";
import styles from "./styles.module.less";

interface TemplatePickerProps {
  onTemplateSelect: (template: MessageTemplate) => void;
  pageId?: string | null;
  activeConversation?: Conversation;
  children: React.ReactNode;
}

export const TemplatePicker = ({
  onTemplateSelect: _onTemplateSelect, // eslint-disable-line @typescript-eslint/no-unused-vars
  pageId,
  activeConversation,
  children,
}: TemplatePickerProps) => {
  const dispatch = useAppDispatch();
  const { error: showError } = useNotificationContext();
  const { pages: facebookPages } = useAppSelector((state) => state.fanpage);
  const messagesStore = useAppSelector((state) => state.messages);
  const selectedFanpageName = useChatStore(
    (state) => state.selectedFanpageName
  );
  const addMessageToStore = useChatStore((state) => state.addMessage);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    null
  );
  const [previewPosition, setPreviewPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const fetchTemplates = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getMessageTemplates({
        offset: 0,
        limit: 20,
      });

      if (response.code === 0 && response.data?.items) {
        setTemplates(response.data.items);
      } else {
        setError("Không thể tải danh sách template");
        setTemplates([]);
      }
    } catch (err) {
      console.error("Error fetching templates:", err);
      setError("Lỗi khi tải danh sách template");
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchTemplates();
    } else {
      setTemplates([]);
      setSearchTerm("");
      setError(null);
    }
  }, [open, pageId]);

  const filteredTemplates = useMemo(() => {
    if (!searchTerm) return templates;

    const lowerSearch = searchTerm.toLowerCase();
    return templates.filter((template) =>
      template.name.toLowerCase().includes(lowerSearch)
    );
  }, [templates, searchTerm]);

  const getTypeIcon = (type: string, color?: string) => {
    const upperType = type.toUpperCase();
    const iconStyle = { color: color || "var(--text-muted-color)" };
    switch (upperType) {
      case "MEDIA":
        return <MediaIcon size={16} style={iconStyle} />;
      case "BUTTON":
        return <ButtonIcon size={16} style={iconStyle} />;
      case "GENERIC":
        return <GenericIcon size={16} style={iconStyle} />;
      default:
        return <GenericIcon size={16} style={iconStyle} />;
    }
  };

  const getTypeColor = (type: string) => {
    const upperType = type.toUpperCase();
    switch (upperType) {
      case "MEDIA":
        return "#2563eb";
      case "BUTTON":
        return "#059669";
      case "GENERIC":
        return "#7c3aed";
      default:
        return "#6b7280";
    }
  };

  const getTypeBackgroundColor = (type: string) => {
    const upperType = type.toUpperCase();
    switch (upperType) {
      case "MEDIA":
        return "linear-gradient(135deg, rgba(37, 99, 235, 0.15) 0%, rgba(37, 99, 235, 0.08) 100%)";
      case "BUTTON":
        return "linear-gradient(135deg, rgba(5, 150, 105, 0.15) 0%, rgba(5, 150, 105, 0.08) 100%)";
      case "GENERIC":
        return "linear-gradient(135deg, rgba(124, 58, 237, 0.15) 0%, rgba(124, 58, 237, 0.08) 100%)";
      default:
        return "linear-gradient(135deg, rgba(107, 114, 128, 0.15) 0%, rgba(107, 114, 128, 0.08) 100%)";
    }
  };

  const handleTemplateClick = (
    template: MessageTemplate,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setSelectedTemplateId(template.id);
    setPreviewPosition({
      top: rect.top + rect.height / 2,
      left: rect.right + 8,
    });
  };

  const handleSendTemplate = async () => {
    if (!selectedTemplateId) return;

    const template = filteredTemplates.find((t) => t.id === selectedTemplateId);
    if (!template) return;
    flushSync(() => {
      setSelectedTemplateId(null);
      setPreviewPosition(null);
    });
    flushSync(() => {
      setOpen(false);
      setSearchTerm("");
    });
    const currentPageId =
      pageId ||
      (selectedFanpageName && facebookPages.length > 0
        ? facebookPages.find((page) => page.name === selectedFanpageName)?.id
        : null);

    if (!currentPageId) {
      showError(
        "Không thể gửi template",
        "Vui lòng chọn fanpage trước khi gửi template."
      );
      return;
    }
    if (!activeConversation) {
      showError(
        "Không thể gửi template",
        "Vui lòng chọn cuộc hội thoại trước khi gửi template."
      );
      return;
    }
    const lastMessage = activeConversation.lastMessage;
    let recipientId: string | undefined;

    if (lastMessage?.meta?.direction === "INBOUND") {
      recipientId = lastMessage.sender.id;
    } else if (lastMessage?.meta?.receiverId) {
      recipientId = lastMessage.meta.receiverId as string;
    }

    if (!recipientId) {
      const conversationExternalId = activeConversation.lastMessage?.conversationId || activeConversation.id;
      if (conversationExternalId) {
        const messages = messagesStore.messagesByConversation[conversationExternalId] || [];
        const message =
          messages.find(
            (msg) => msg.meta?.direction === "INBOUND" && msg.sender?.id
          ) || messages.find((msg) => msg.meta?.receiverId);
        recipientId =
          message?.meta?.direction === "INBOUND"
            ? message.sender.id
            : (message?.meta?.receiverId as string | undefined);
      }
    }

    if (!recipientId) {
      const selectedPage = facebookPages.find(
        (page) => page.name === selectedFanpageName
      );
      const member = activeConversation.members?.find(
        (m) => m.id !== selectedPage?.id
      );
      recipientId = member?.id;
    }

    if (!recipientId) {
      showError(
        "Không thể gửi template",
        "Không tìm thấy thông tin người nhận. Vui lòng thử lại sau khi tải lại trang hoặc chọn lại cuộc hội thoại."
      );
      return;
    }

    let templatePayload: Record<string, unknown> = {};
    try {
      templatePayload =
        typeof template.payload === "string"
          ? JSON.parse(template.payload)
          : template.payload;
    } catch {
      templatePayload = {};
    }

    let messageBody = template.name;
    if (templatePayload.text) {
      messageBody = templatePayload.text as string;
    } else if (templatePayload.title) {
      messageBody = templatePayload.title as string;
      if (templatePayload.subtitle) {
        messageBody += `\n${templatePayload.subtitle}`;
      }
    } else if (
      templatePayload.elements &&
      Array.isArray(templatePayload.elements) &&
      templatePayload.elements[0]
    ) {
      const firstElement = templatePayload.elements[0] as Record<
        string,
        unknown
      >;
      messageBody = (firstElement.title as string) || template.name;
      if (firstElement.subtitle) {
        messageBody += `\n${firstElement.subtitle}`;
      }
    }

    const conversationId =
      activeConversation.lastMessage?.conversationId || activeConversation.id;

    const tempMessageId = `temp-template-${Date.now()}-${Math.random()}`;
    const optimisticMessage: Message = {
      id: tempMessageId,
      conversationId: conversationId,
      sender: {
        id: "current-user",
        name: "Bạn",
        avatar: "",
      },
      body: messageBody,
      createdAt: new Date().toISOString(),
      status: "sending",
      type: "text",
      meta: {
        direction: "OUTBOUND",
        provider: "facebook",
        isTemplate: true,
        templateId: template.id,
        templateName: template.name,
        templateType: template.type,
        templatePayload: templatePayload,
      },
    };

    dispatch(
      addMessage({
        conversationId: conversationId,
        message: optimisticMessage,
      })
    );

    addMessageToStore(conversationId, optimisticMessage, true);

    try {
      const response = await sendMessageTemplate({
        templateId: selectedTemplateId,
        recipientId,
        pageId: currentPageId,
      });

      if (response.code === 0 || response.code === 200) {
        // Get actorSource from JWT token
        const token = getLocalStorage(STORAGE_KEYS.AUTH_TOKEN);
        const actorSource = getDisplayNameFromToken(token || null);
        
        const finalMessage: Message = {
          ...optimisticMessage,
          status: "sent",
          meta: {
            ...optimisticMessage.meta,
            ...(actorSource && { actorSource }),
          },
        };

        dispatch(
          addMessage({
            conversationId: conversationId,
            message: finalMessage,
          })
        );

        addMessageToStore(conversationId, finalMessage, true);

        // Popover và preview đã được đóng ở đầu hàm handleSendTemplate
      } else {
        dispatch(
          addMessage({
            conversationId: conversationId,
            message: {
              ...optimisticMessage,
              status: "sending",
            },
          })
        );

        showError(
          "Gửi template thất bại",
          response.message || "Không thể gửi template. Vui lòng thử lại."
        );
      }
    } catch (err) {
      console.error("Error sending template:", err);

      showError(
        "Lỗi hệ thống",
        "Đã xảy ra lỗi khi gửi template. Vui lòng thử lại."
      );
    }
  };

  const handleCancelPreview = () => {
    setSelectedTemplateId(null);
    setPreviewPosition(null);
  };

  const content = (
    <div className={styles.templatePicker}>
      <div className={styles.templatePickerHeader}>
        <Input
          placeholder="Tìm kiếm template..."
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onPressEnter={fetchTemplates}
          allowClear
        />
      </div>

      <div className={styles.templatePickerContent}>
        {loading ? (
          <div className={styles.templatePickerLoading}>
            <Spin size="small" />
            <span>Đang tải...</span>
          </div>
        ) : error ? (
          <div className={styles.templatePickerError}>{error}</div>
        ) : filteredTemplates.length === 0 ? (
          <Empty
            description={
              searchTerm ? "Không tìm thấy template" : "Không có template nào"
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <div className={styles.templateList}>
            {filteredTemplates.map((template) => (
              <div
                key={template.id}
                ref={(el) => {
                  if (el) {
                    itemRefs.current.set(template.id, el);
                  } else {
                    itemRefs.current.delete(template.id);
                  }
                }}
                className={classNames(styles.templateItem, {
                  [styles.templateItemActive]:
                    selectedTemplateId === template.id,
                })}
                onClick={(e) => handleTemplateClick(template, e)}
              >
                <div className={styles.templateItemContent}>
                  {template.type && (
                    <div
                      className={styles.templateIcon}
                      style={{
                        color: getTypeColor(template.type),
                        background: getTypeBackgroundColor(template.type),
                      }}
                    >
                      {getTypeIcon(template.type, getTypeColor(template.type))}
                    </div>
                  )}
                  <div className={styles.templateInfo}>
                    <div className={styles.templateName}>{template.name}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {selectedTemplateId &&
        previewPosition &&
        filteredTemplates.find((t) => t.id === selectedTemplateId) &&
        createPortal(
          <div
            className={styles.templatePreviewWrapper}
            style={{
              top: `${previewPosition.top}px`,
              left: `${previewPosition.left}px`,
              transform: "translateY(-50%)",
            }}
          >
            <TemplatePreview
              template={
                filteredTemplates.find((t) => t.id === selectedTemplateId)!
              }
              onSend={handleSendTemplate}
              onCancel={handleCancelPreview}
            />
          </div>,
          document.body
        )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={(newOpen) => {
        setOpen(newOpen);
        if (!newOpen) {
          setSearchTerm("");
          setError(null);
        }
      }}
      placement="top"
      overlayClassName={styles.templatePickerOverlay}
      getPopupContainer={(trigger) => trigger.parentElement || document.body}
      align={{ offset: [0, -8] }}
      styles={{ body: { padding: 0 } }}
    >
      {children}
    </Popover>
  );
};
